/**
 * Backend error strings the console recognises.
 *
 * These are matched rather than read from an error code because sdapi does not yet
 * put a code in the GraphQL `extensions` block. Each literal must stay byte-identical
 * to the constant in the owning service — reword one side and the console silently
 * falls back to a generic message, which is why both sides pin it in a test.
 */

/** phoenix-jobs: PIPELINE_EXECUTION_ALREADY_RUNNING_MESSAGE in trigger_pipeline_execution.py */
export const PIPELINE_EXECUTION_ALREADY_RUNNING_ERROR =
  'There is already a running execution for this pipeline';

const readErrorMessage = (error: unknown): string => {
  const message =
    error instanceof Error
      ? error.message
      : ((error as { message?: unknown })?.message ?? '');

  return typeof message === 'string' ? message : '';
};

export const isPipelineExecutionAlreadyRunningError = (
  error: unknown,
): boolean =>
  readErrorMessage(error).includes(PIPELINE_EXECUTION_ALREADY_RUNNING_ERROR);

/**
 * back-office: FeatureFlagStillAssignedError, raised in
 * services/super_tenant/delete_feature_flag.py as
 * `Feature flag {name} is still assigned to {n} tenant(s)`.
 *
 * The flag name and the count are the only variable parts, so the whole sentence is
 * pinned as a pattern rather than the bare phrase: `shouldSuppressError` swallows
 * whatever matches for every query in both apps, and "is still assigned to" is a
 * fragment any service could produce about a secret, a role or a licence.
 */
export const FEATURE_FLAG_STILL_ASSIGNED_PATTERN =
  /Feature flag .+ is still assigned to (\d+) tenant\(s\)/;

export const isFeatureFlagStillAssignedMessage = (message: string): boolean =>
  FEATURE_FLAG_STILL_ASSIGNED_PATTERN.test(message);

/**
 * How many tenants the guard reported, or undefined when the error is not that
 * refusal. The count comes from the same pinned sentence the refusal is
 * recognised by, so it is exactly as reliable as the recognition itself.
 */
export const readStillAssignedTenantCount = (
  error: unknown,
): number | undefined => {
  const match = readErrorMessage(error).match(
    FEATURE_FLAG_STILL_ASSIGNED_PATTERN,
  );

  return match ? Number(match[1]) : undefined;
};

export const isFeatureFlagStillAssignedError = (error: unknown): boolean =>
  readStillAssignedTenantCount(error) !== undefined;
